/**
 * Risk Redux Slice
 *
 * Normalized state management for risks using createEntityAdapter.
 * Provides:
 * - Normalized entity storage
 * - CRUD operations
 * - Filtering and sorting
 * - Pagination state
 * - Selectors for computed data
 *
 * @module entities/risk/model/riskSlice
 */

import { createSlice, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { riskApi } from '../api/riskApi';
import { archiveArgs } from '../../../shared/api/archiveArgs';
import { calculateRiskStatistics, prioritizeRisks } from '../lib/riskCalculations';

// ============================================================================
// Entity Adapter
// ============================================================================

/**
 * Entity adapter for normalized risk storage
 * Provides CRUD methods and selectors
 */
export const riskAdapter = createEntityAdapter({
  // Use 'id' (UUID primary key) as the entity key
  selectId: (risk) => risk.id,
  // Sort by risk score (descending) by default
  sortComparer: (a, b) => b.inherent_risk_score - a.inherent_risk_score,
});

// ============================================================================
// Initial State
// ============================================================================

const initialState = riskAdapter.getInitialState({
  // Loading states
  loading: false,
  error: null,

  // Filters
  filters: {
    search: '',
    classification: 'all',
    level: 'all',
    status: 'all',
    owner: null,
    department: null,
    dateRange: {
      from: null,
      to: null,
    },
  },

  // Sorting
  sortBy: 'inherent_risk_score',
  sortOrder: 'desc',

  // Pagination
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },

  // UI State
  selectedRiskId: null,
  viewMode: 'grid', // 'grid', 'list', 'table'

  // Statistics cache
  statistics: null,
  statisticsLastUpdated: null,
});

// ============================================================================
// Slice
// ============================================================================

export const riskSlice = createSlice({
  name: 'risks',
  initialState,
  reducers: {
    // Filter actions
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
      state.pagination.page = 1; // Reset to first page
    },

    setClassificationFilter: (state, action) => {
      state.filters.classification = action.payload;
      state.pagination.page = 1;
    },

    setLevelFilter: (state, action) => {
      state.filters.level = action.payload;
      state.pagination.page = 1;
    },

    setStatusFilter: (state, action) => {
      state.filters.status = action.payload;
      state.pagination.page = 1;
    },

    setOwnerFilter: (state, action) => {
      state.filters.owner = action.payload;
      state.pagination.page = 1;
    },

    setDepartmentFilter: (state, action) => {
      state.filters.department = action.payload;
      state.pagination.page = 1;
    },

    setDateRangeFilter: (state, action) => {
      state.filters.dateRange = action.payload;
      state.pagination.page = 1;
    },

    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
    },

    // Sorting actions
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },

    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },

    toggleSortOrder: (state) => {
      state.sortOrder = state.sortOrder === 'asc' ? 'desc' : 'asc';
    },

    // Pagination actions
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },

    setPageLimit: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1; // Reset to first page
    },

    nextPage: (state) => {
      if (state.pagination.page < state.pagination.totalPages) {
        state.pagination.page += 1;
      }
    },

    previousPage: (state) => {
      if (state.pagination.page > 1) {
        state.pagination.page -= 1;
      }
    },

    // UI actions
    setSelectedRisk: (state, action) => {
      state.selectedRiskId = action.payload;
    },

    clearSelectedRisk: (state) => {
      state.selectedRiskId = null;
    },

    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },

    // Statistics actions
    updateStatistics: (state, action) => {
      state.statistics = action.payload;
      state.statisticsLastUpdated = Date.now();
    },
  },

  // Handle RTK Query actions
  extraReducers: (builder) => {
    // Get risks - fulfilled
    builder.addMatcher(riskApi.endpoints.getRisks.matchFulfilled, (state, action) => {
      riskAdapter.setAll(state, action.payload.data);
      state.pagination.total = action.payload.total || action.payload.data.length;
      state.pagination.totalPages = Math.ceil(state.pagination.total / state.pagination.limit);
      state.loading = false;
      state.error = null;
    });

    // Get risks - pending
    builder.addMatcher(riskApi.endpoints.getRisks.matchPending, (state) => {
      state.loading = true;
      state.error = null;
    });

    // Get risks - rejected
    builder.addMatcher(riskApi.endpoints.getRisks.matchRejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // Get risk by ID - fulfilled
    builder.addMatcher(riskApi.endpoints.getRiskById.matchFulfilled, (state, action) => {
      riskAdapter.upsertOne(state, action.payload);
    });

    // Create risk - fulfilled
    builder.addMatcher(riskApi.endpoints.createRisk.matchFulfilled, (state, action) => {
      riskAdapter.addOne(state, action.payload);
      state.pagination.total += 1;
      state.pagination.totalPages = Math.ceil(state.pagination.total / state.pagination.limit);
    });

    // Update risk - fulfilled
    builder.addMatcher(riskApi.endpoints.updateRisk.matchFulfilled, (state, action) => {
      riskAdapter.updateOne(state, {
        id: action.meta.arg.originalArgs.id,
        changes: action.payload,
      });
    });

    // Delete risk - fulfilled
    builder.addMatcher(riskApi.endpoints.deleteRisk.matchFulfilled, (state, action) => {
      // D24: originalArgs is `id` or `{ id, reason }`.
      riskAdapter.removeOne(state, archiveArgs(action.meta.arg.originalArgs).id);
      state.pagination.total -= 1;
      state.pagination.totalPages = Math.ceil(state.pagination.total / state.pagination.limit);
    });

    // Get statistics/summary - fulfilled
    builder.addMatcher(riskApi.endpoints.getRiskSummary.matchFulfilled, (state, action) => {
      state.statistics = action.payload;
      state.statisticsLastUpdated = Date.now();
    });
  },
});

// ============================================================================
// Actions
// ============================================================================

export const {
  setSearchFilter,
  setClassificationFilter,
  setLevelFilter,
  setStatusFilter,
  setOwnerFilter,
  setDepartmentFilter,
  setDateRangeFilter,
  clearFilters,
  setSortBy,
  setSortOrder,
  toggleSortOrder,
  setPage,
  setPageLimit,
  nextPage,
  previousPage,
  setSelectedRisk,
  clearSelectedRisk,
  setViewMode,
  updateStatistics,
} = riskSlice.actions;

// ============================================================================
// Base Selectors (from adapter)
// ============================================================================

export const riskSelectors = riskAdapter.getSelectors((state) => state.risks);

// Adapter-generated selectors
export const {
  selectAll: selectAllRisks,
  selectById: selectRiskById,
  selectIds: selectRiskIds,
  selectEntities: selectRiskEntities,
  selectTotal: selectTotalRisks,
} = riskSelectors;

// ============================================================================
// Custom Selectors
// ============================================================================

/**
 * Select loading state
 */
export const selectRisksLoading = (state) => state.risks.loading;

/**
 * Select error state
 */
export const selectRisksError = (state) => state.risks.error;

/**
 * Select all active filters
 */
export const selectRiskFilters = (state) => state.risks.filters;

/**
 * Select sort configuration
 */
export const selectRiskSort = (state) => ({
  sortBy: state.risks.sortBy,
  sortOrder: state.risks.sortOrder,
});

/**
 * Select pagination state
 */
export const selectRiskPagination = (state) => state.risks.pagination;

/**
 * Select view mode
 */
export const selectRiskViewMode = (state) => state.risks.viewMode;

/**
 * Select currently selected risk ID
 */
export const selectSelectedRiskId = (state) => state.risks.selectedRiskId;

/**
 * Select currently selected risk object
 */
export const selectSelectedRisk = createSelector(
  [selectRiskEntities, selectSelectedRiskId],
  (entities, selectedId) => (selectedId ? entities[selectedId] : null)
);

/**
 * Select statistics
 */
export const selectRiskStatistics = (state) => state.risks.statistics;

/**
 * Select filtered risks based on active filters
 */
export const selectFilteredRisks = createSelector(
  [selectAllRisks, selectRiskFilters, selectRiskSort],
  (risks, filters, sort) => {
    let filtered = [...risks];

    // Apply search filter
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(
        (risk) =>
          risk.title.toLowerCase().includes(search) ||
          risk.description?.toLowerCase().includes(search) ||
          risk.owner.toLowerCase().includes(search) ||
          risk.department?.toLowerCase().includes(search)
      );
    }

    // Apply classification (Master Data Level 1) filter
    if (filters.classification && filters.classification !== 'all') {
      filtered = filtered.filter((risk) => risk.classification_name === filters.classification);
    }

    // Apply level filter
    if (filters.level && filters.level !== 'all') {
      filtered = filtered.filter((risk) => risk.risk_level === filters.level);
    }

    // Apply status filter
    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter((risk) => risk.status === filters.status);
    }

    // Apply owner filter
    if (filters.owner) {
      filtered = filtered.filter((risk) => risk.owner === filters.owner);
    }

    // Apply department filter
    if (filters.department) {
      filtered = filtered.filter((risk) => risk.department === filters.department);
    }

    // Apply date range filter
    if (filters.dateRange.from || filters.dateRange.to) {
      filtered = filtered.filter((risk) => {
        const riskDate = new Date(risk.identified_date);
        if (filters.dateRange.from && riskDate < new Date(filters.dateRange.from)) {
          return false;
        }
        if (filters.dateRange.to && riskDate > new Date(filters.dateRange.to)) {
          return false;
        }
        return true;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sort.sortBy];
      let bVal = b[sort.sortBy];

      // Handle string comparisons
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sort.sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }
);

/**
 * Select paginated risks for current page
 */
export const selectPaginatedRisks = createSelector(
  [selectFilteredRisks, selectRiskPagination],
  (filteredRisks, pagination) => {
    const start = (pagination.page - 1) * pagination.limit;
    const end = start + pagination.limit;
    return filteredRisks.slice(start, end);
  }
);

/**
 * Select risks grouped by classification (Master Data Level 1, falling back
 * to a legacy risk's old category -- see RiskListSerializer.classification_name)
 */
export const selectRisksByClassification = createSelector([selectAllRisks], (risks) => {
  return risks.reduce((acc, risk) => {
    const key = risk.classification_name || 'Unclassified';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(risk);
    return acc;
  }, {});
});

/**
 * Select risks by level (grouped)
 */
export const selectRisksByLevel = createSelector([selectAllRisks], (risks) => {
  return risks.reduce((acc, risk) => {
    if (!acc[risk.risk_level]) {
      acc[risk.risk_level] = [];
    }
    acc[risk.risk_level].push(risk);
    return acc;
  }, {});
});

/**
 * Select risks by owner (grouped)
 */
export const selectRisksByOwner = createSelector([selectAllRisks], (risks) => {
  return risks.reduce((acc, risk) => {
    if (!acc[risk.owner]) {
      acc[risk.owner] = [];
    }
    acc[risk.owner].push(risk);
    return acc;
  }, {});
});

/**
 * Select critical risks (Critical + High level)
 */
export const selectCriticalRisks = createSelector([selectAllRisks], (risks) =>
  risks.filter((risk) => risk.risk_level === 'Critical' || risk.risk_level === 'High')
);

/**
 * Select open risks
 */
export const selectOpenRisks = createSelector([selectAllRisks], (risks) =>
  risks.filter((risk) => risk.status === 'Open')
);

/**
 * Select overdue risks (review date passed)
 */
export const selectOverdueRisks = createSelector([selectAllRisks], (risks) =>
  risks.filter((risk) => {
    if (!risk.next_review_date) return false;
    return new Date(risk.next_review_date) < new Date();
  })
);

/**
 * Select risks exceeding risk appetite
 */
export const selectRisksExceedingAppetite = createSelector(
  [selectAllRisks],
  (risks) => risks.filter((risk) => risk.inherent_risk_score > 12) // Default threshold
);

/**
 * Select prioritized risks (using smart algorithm)
 */
export const selectPrioritizedRisks = createSelector([selectAllRisks], (risks) =>
  prioritizeRisks(risks)
);

/**
 * Select computed statistics
 */
export const selectComputedStatistics = createSelector([selectAllRisks], (risks) =>
  calculateRiskStatistics(risks)
);

/**
 * Select filter active state (any filters applied?)
 */
export const selectHasActiveFilters = createSelector([selectRiskFilters], (filters) => {
  return (
    filters.search !== '' ||
    (filters.classification ?? 'all') !== 'all' ||
    filters.level !== 'all' ||
    filters.status !== 'all' ||
    filters.owner !== null ||
    filters.department !== null ||
    filters.dateRange.from !== null ||
    filters.dateRange.to !== null
  );
});

// ============================================================================
// Reducer Export
// ============================================================================

export default riskSlice.reducer;
